// UI strings of the app shell itself: the error screens, the 404 page, the skip link and the
// document titles. Finnish (default) and English side by side; en must have exactly the same shape.
// Finnish follows docs/FINNISH-STYLE.md: no internal jargon, "sinä" form.
import { defineArea } from '../area';

export const shellFi = {
  /** Skip link before the navigation (keyboard and screen reader users). */
  skip: 'Siirry sisältöön',
  /** A new device without the dictionary and without a connection (the dictionary is not in the offline cache). */
  dictOffline: 'Sanakirjaa ei ole vielä ladattu tälle laitteelle. Yhdistä verkkoon, niin se latautuu – sen jälkeen sovellus toimii myös ilman yhteyttä.',
  /** document.title: "<näkymä> – Valyrio udrir". */
  title: (view: string): string => `${view} – Valyrio udrir`,
  error: {
    title: 'Tämä näkymä kaatui',
    lead: 'Muut osat toimivat, ja korttisi ja arviosi ovat tallessa tällä laitteella.',
    appTitle: 'Sovellus kaatui',
    appLead: 'Korttisi ja arviosi ovat tallessa tällä laitteella. Yritä uudelleen; jos sama toistuu, tyhjennä välimuisti.',
    chunkTitle: 'Näkymää ei saatu ladattua',
    chunkLead: 'Yhteys katkesi tai sovellus päivittyi kesken. Tarkista yhteys ja yritä uudelleen.',
    retry: 'Yritä uudelleen',
    home: 'Palaa Tänään-sivulle',
    clearCache: 'Tyhjennä välimuisti',
    clearCacheNote:
      'Jos virhe toistuu, tyhjennä välimuisti: palvelimelta haetut listat (kirjeet, merkinnät, ehdotukset, äänitteet) poistetaan tältä laitteelta ja haetaan uudelleen. Korttisi, arviosi ja lähettämättömät muutokset säilyvät.',
    clearing: 'Tyhjennetään…',
    details: 'Tiedot virheestä',
    detailsNote: 'Jos kerrot virheestä, liitä mukaan alla oleva teksti.',
  },
  notFound: {
    title: 'Sivua ei löydy',
    lead: 'Tällä osoitteella ei ole näkymää. Linkki voi olla vanhentunut, tai osoitteessa on kirjoitusvirhe.',
    home: 'Palaa Tänään-sivulle',
    more: 'Kaikki näkymät',
  },
};

export const shellEn: typeof shellFi = {
  skip: 'Skip to content',
  dictOffline:
    'The dictionary has not been downloaded to this device yet. Connect to the internet and it will download – after that the app works offline too.',
  title: (view: string): string => `${view} – Valyrio udrir`,
  error: {
    title: 'This view crashed',
    lead: 'The rest of the app works, and your cards and grades are saved on this device.',
    appTitle: 'The app crashed',
    appLead: 'Your cards and grades are saved on this device. Try again; if it happens again, clear the cache.',
    chunkTitle: 'This view could not be loaded',
    chunkLead: 'The connection dropped or the app was updated meanwhile. Check the connection and try again.',
    retry: 'Try again',
    home: 'Back to Today',
    clearCache: 'Clear cache',
    clearCacheNote:
      'If the error comes back, clear the cache: the lists fetched from the server (letters, flags, proposals, recordings) are removed from this device and fetched again. Your cards, grades and unsent changes stay.',
    clearing: 'Clearing…',
    details: 'Error details',
    detailsNote: 'If you report this error, include the text below.',
  },
  notFound: {
    title: 'Page not found',
    lead: 'There is no view at this address. The link may be out of date, or the address has a typo.',
    home: 'Back to Today',
    more: 'All views',
  },
};

// Messages.shell (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('shell', shellFi, shellEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof shellFi> {}
}
